import { put, select, takeLatest } from 'redux-saga/effects'
import { setComments, saveVitals } from '../store/vitals.actions'
import { VitalsConstants } from '../store/vitals.constants'
import { IScaleSelection, IPatientVitals, IScaleOptionDefinitionList } from '../store/vitals.types'
import { getPatientVitalsSelector } from '../selectors/patientVitals.selector'
import { getVitalOptionsSelector } from '../../options/selectors'
import { IVitalOptionsState } from '../../options/store/options.types'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'

const SEDATION_PREFIX = 'Sedation Scale Score:'
const SEDATION_BLOCK_PATTERN = /Sedation Scale Score:(?:\s*[^;]+;)+/

const buildSedationBlock = (selections: IScaleSelection[], scales: IScaleOptionDefinitionList): string => {
  if (!selections.length) return ''

  const byScale = selections.reduce<Record<number, IScaleSelection[]>>((acc, s) => {
    ;(acc[s.scaleId] ??= []).push(s)
    return acc
  }, {})

  const parts = Object.entries(byScale).flatMap(([scaleId, items]) => {
    const scale = scales.find((s) => s.scaleId === Number(scaleId))
    if (!scale) return []

    const sectionText = items.map((i) => `${i.sectionLabel}: ${i.name}`).join(', ')
    return [`${scale.scaleLabel} score values are ${sectionText};`]
  })

  return `${SEDATION_PREFIX} ${parts.join(' ')}`
}

const upsertSedationBlock = (comments: string, sedationBlock: string): string => {
  const match = comments.match(SEDATION_BLOCK_PATTERN)

  if (match) {
    // Sedation line exists then replace the matching sedation section else remove it
    const updated = sedationBlock ? comments.replace(match[0], sedationBlock) : comments.replace(match[0], '').trim()
    return updated
  }

  if (!sedationBlock) return comments

  const trimmed = comments.trimEnd()
  return trimmed ? `${trimmed} ${sedationBlock}` : sedationBlock
}

export function* setSedationScaleComments() {
  const vitals: IPatientVitals = yield select(getPatientVitalsSelector)
  const vitalOptions: IVitalOptionsState = yield select(getVitalOptionsSelector)

  const selections = vitals?.sedationScale?.selections ?? []
  const sedationBlock = buildSedationBlock(selections, vitalOptions.sedationScaleOptions)

  const updatedComments = upsertSedationBlock(vitals.comments, sedationBlock)

  if (updatedComments !== vitals.comments) {
    yield put(setComments(updatedComments))
  }

  yield put(saveVitals())
}

export function* setSedationScaleCommentsSaga() {
  yield takeLatest([VitalsConstants.SET_SEDATION_SCALE], performWithMutexHOG, setSedationScaleComments)
}
